import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, AlertCircle } from 'lucide-react';
import '../styles/noisefast.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NoiseFastStartPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    leak_description: '',
    leak_type: '',
    can_start_in_10_min: null,
    done_looks_like: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSmallerWarning, setShowSmallerWarning] = useState(false);

  const leakTypes = [
    'Physical clutter',
    'Digital clutter',
    'Financial/admin',
    'Health/wellness',
    'Relationship/conversation',
    'Decision I\'ve been avoiding',
    'Mental clutter',
    'Other'
  ];

  const helperExamples = [
    'clear the counter',
    'gather tax papers',
    'cancel subscriptions',
    'delete apps',
    'schedule appointment',
    'sort mail',
    'clean out a drawer',
    'ask HR about wellness benefits',
    'update software',
    'send the email'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'can_start_in_10_min') {
      setShowSmallerWarning(value === false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.leak_description || !formData.leak_type || formData.can_start_in_10_min === null || !formData.done_looks_like) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (formData.can_start_in_10_min === false) {
      setError('Please make your task smaller before continuing');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/noisefast/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          stripe_session_id: searchParams.get('session_id') || null
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit');
      
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="nf-page nf-start-page">
        <header className="nf-header">
          <Link to="/" className="nf-logo">GoCalFit</Link>
          <span className="nf-nav-marker">[ noise fast ]</span>
        </header>

        <section className="nf-start-hero">
          <div className="nf-start-content">
            <div className="nf-success-icon">
              <Check size={48} />
            </div>
            
            <h1>You're locked in.</h1>
            
            <p className="nf-start-sub">Check your email for Day 1.</p>
            
            <div className="nf-reminder-box">
              <h3>Remember the deal:</h3>
              <ul>
                <li>No social media</li>
                <li>No podcasts</li>
                <li>No background noise</li>
                <li>No loopholes</li>
              </ul>
              <p className="nf-highlight">If you follow it, it works.</p>
            </div>
            
            <div className="nf-final-section">
              <h2>This probably isn't the only leak.</h2>
              
              <p>Most people don't just have one.</p>
              
              <p className="nf-dim">They have patterns.</p>
              
              <div className="nf-patterns">
                <p>Patterns in:</p>
                <ul>
                  <li>attention</li>
                  <li>energy</li>
                  <li>avoidance</li>
                  <li>overwhelm</li>
                  <li>decision-making</li>
                  <li>follow-through</li>
                </ul>
              </div>
              
              <p><strong>Noise Fast</strong> helps you close one.</p>
              <p><strong>Personal Operations</strong> helps you understand the rest.</p>
              
              <div className="nf-for-now">
                <p>For now:</p>
                <p className="nf-highlight">one leak. one action. one reset.</p>
              </div>
            </div>
          </div>
        </section>

        <footer className="nf-footer">
          <Link to="/" className="nf-footer-logo">GoCalFit</Link>
          <p>Serious movement. Serious play.</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="nf-page nf-start-page">
      <header className="nf-header">
        <Link to="/" className="nf-logo">GoCalFit</Link>
        <span className="nf-nav-marker">[ noise fast ]</span>
      </header>

      {/* Hero */}
      <section className="nf-start-hero">
        <div className="nf-start-content">
          <span className="nf-section-num">[ before we start ]</span>
          
          <h1>Pick your leak.</h1>
          
          <p className="nf-start-sub">
            Most of the things draining your energy take less than 10 minutes to begin.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="nf-section nf-story">
        <div className="nf-section-inner">
          <p>This morning mine was clearing the patio outside my office.</p>
          
          <p className="nf-dim">Every time I looked at it, I felt stress.</p>
          
          <p className="nf-dim">
            Not dramatic stress.<br />
            Just enough irritation and anxiety to pull me out of focus.
          </p>
          
          <div className="nf-math">
            <p>Probably 90 seconds to 2 minutes of mental drain.</p>
            <p>At least 5 times a day.</p>
          </div>
          
          <p className="nf-highlight">
            That's WAY more energy than the 7.5 minutes it took me to actually clear it.
          </p>
          
          <p className="nf-bold nf-large">That's a leak.</p>
          <p className="nf-emphasis">That's what we're fixing.</p>
        </div>
      </section>

      {/* Form Section */}
      <section className="nf-section nf-form-section">
        <div className="nf-section-inner">
          <h2>What's quietly draining your energy?</h2>
          
          <form onSubmit={handleSubmit} className="nf-form">
            {/* Question 1 */}
            <div className="nf-field">
              <label>What's one thing you've been avoiding?</label>
              <textarea
                value={formData.leak_description}
                onChange={(e) => handleInputChange('leak_description', e.target.value)}
                placeholder="The thing that keeps nagging at the back of your mind..."
                rows={3}
              />
            </div>
            
            {/* Question 2 */}
            <div className="nf-field">
              <label>Which type of energy leak is it?</label>
              <select
                value={formData.leak_type}
                onChange={(e) => handleInputChange('leak_type', e.target.value)}
              >
                <option value="">Select one...</option>
                {leakTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            {/* Question 3 */}
            <div className="nf-field">
              <label>Can this be STARTED in 10 minutes or less?</label>
              <div className="nf-radio-group">
                <button
                  type="button"
                  className={`nf-radio-btn ${formData.can_start_in_10_min === true ? 'active' : ''}`}
                  onClick={() => handleInputChange('can_start_in_10_min', true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`nf-radio-btn ${formData.can_start_in_10_min === false ? 'active' : ''}`}
                  onClick={() => handleInputChange('can_start_in_10_min', false)}
                >
                  No
                </button>
              </div>
              
              {showSmallerWarning && (
                <div className="nf-warning">
                  <AlertCircle size={20} />
                  <p>Make it smaller. We are building momentum, not perfection.</p>
                </div>
              )}
            </div>
            
            {/* Question 4 */}
            <div className="nf-field">
              <label>What would "done for now" look like?</label>
              <textarea
                value={formData.done_looks_like}
                onChange={(e) => handleInputChange('done_looks_like', e.target.value)}
                placeholder="Describe what 'handled' looks like..."
                rows={2}
              />
              <div className="nf-helper">
                <p>Examples:</p>
                <div className="nf-examples">
                  {helperExamples.map(ex => (
                    <span key={ex}>{ex}</span>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Question 5 - Optional */}
            <div className="nf-field nf-optional">
              <label>
                Want text reminders during Noise Fast?
                <span className="nf-optional-tag">optional</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
            
            {error && (
              <div className="nf-error">
                <AlertCircle size={18} />
                <p>{error}</p>
              </div>
            )}
            
            <button 
              type="submit" 
              className="nf-cta nf-form-submit"
              disabled={loading}
            >
              {loading ? 'Locking in...' : 'Lock it in'} <ArrowRight size={20} />
            </button>
          </form>
        </div>
      </section>

      <footer className="nf-footer">
        <Link to="/" className="nf-footer-logo">GoCalFit</Link>
        <p>Serious movement. Serious play.</p>
      </footer>
    </div>
  );
};

export default NoiseFastStartPage;
